import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type DeadlineDraft, type DeadlineErrorCode, rangeKind } from '@/shared/lib/deadline-draft';

import styles from './DateProperty.module.css';
import { DeadlineFields } from './DeadlineFields';
import { HelpTip } from './HelpTip';
import { Icons } from './icons';

interface Props {
  value: DeadlineDraft;
  onChange: (next: DeadlineDraft) => void;
  errors: readonly DeadlineErrorCode[];
  timezone: string | null;
}

function format(date: string, time: string, locale: string): string {
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  const day = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: parsed.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  }).format(parsed);
  return time ? `${day}, ${time.slice(0, 5)}` : day;
}

/**
 * The "Dates" property: one compact row (title, a one-line summary, a chevron) that is COLLAPSED
 * until the user opens it. Collapsing never discards the draft. While the draft is incomplete the
 * section stays open, so the missing field is in front of the user. Start and End are always shown
 * together when open (see `DeadlineFields`).
 */
export function DateProperty({ value, onChange, errors, timezone }: Props) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [open, setOpen] = useState(false);
  const kind = rangeKind(value);
  const shown = open || errors.length > 0;

  let summary: string;
  if (errors.length > 0 || kind === 'invalid') summary = t('deadline.summary.incomplete');
  else if (kind === 'none') summary = t('deadline.summary.none');
  else {
    const end = format(value.endDate, value.endTime, i18n.language);
    summary =
      kind === 'event'
        ? `${format(value.startDate, value.startTime, i18n.language)} → ${end}`
        : end;
  }

  return (
    <section className={styles.root} aria-labelledby={`${id}-title`}>
      <div className={styles.head}>
        <h3 id={`${id}-title`} className={styles.title}>
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={shown}
            aria-controls={`${id}-panel`}
            onClick={() => {
              setOpen((current) => !current);
            }}
          >
            <span className={styles.name}>{t('deadline.sectionTitle')}</span>
            <span className={styles.summary} data-kind={kind} data-testid="date-summary">
              {summary}
            </span>
            <span className={`${styles.chevron} ${shown ? styles.chevronOpen : ''}`}>
              <Icons.chevronDown />
            </span>
          </button>
        </h3>
        <HelpTip
          topic={t('deadline.sectionTitle')}
          topicId="calendar-timeline"
          text={t('help.dates')}
        />
      </div>
      <div id={`${id}-panel`} className={styles.panel} hidden={!shown}>
        <DeadlineFields value={value} onChange={onChange} errors={errors} timezone={timezone} />
      </div>
    </section>
  );
}
